import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
} from "@angular/core";
import {
  ContentActivityEntry,
  ContentFieldChange,
} from "src/app/Services/content-activity.service";

const COLLAPSED_CHANGE_COUNT = 4;

@Component({
  selector: "app-activity-timeline",
  templateUrl: "./activity-timeline.component.html",
  styleUrls: ["./activity-timeline.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ActivityTimelineComponent {
  @Input() entries: ContentActivityEntry[] = [];
  /** Org audit view: show which guide / guide pack each entry is about. */
  @Input() showTarget = false;
  @Output() openTarget = new EventEmitter<ContentActivityEntry>();

  private expanded = new Set<string>();

  trackById(index: number, entry: ContentActivityEntry): string {
    return entry.id || `${index}`;
  }

  actorLabel(entry: ContentActivityEntry): string {
    return entry.actorName || entry.actorEmail || "Someone";
  }

  initials(entry: ContentActivityEntry): string {
    const source = (this.actorLabel(entry) || "?").trim();
    const parts = source.split(/[\s@._-]+/).filter(Boolean);
    const letters = (parts[0]?.[0] || "?") + (parts[1]?.[0] || "");
    return letters.toUpperCase();
  }

  categoryIcon(entry: ContentActivityEntry): string {
    switch (entry.category) {
      case "Approval":
        return "fact_check";
      case "Access":
        return "lock";
      case "Edit":
        return "edit_note";
      default:
        return this.targetKind(entry) === "Member" ? "person" : "history";
    }
  }

  categoryLabel(entry: ContentActivityEntry): string {
    switch (entry.category) {
      case "Approval":
        return "Approval";
      case "Access":
        return "Access";
      case "Edit":
        return "Content";
      default:
        return this.targetKind(entry) || "Organization";
    }
  }

  actionTone(entry: ContentActivityEntry): string {
    const action = (entry.action || "").toLowerCase();
    if (action.includes("reject") || action.includes("deleted") || action.includes("removed"))
      return "danger";
    if (action.includes("approved") || action.includes("publish")) return "success";
    if (action.includes("submitted") || action.includes("review")) return "info";
    return "neutral";
  }

  targetKind(entry: ContentActivityEntry): string {
    const t = (entry as any).targetType || entry.contentType || "";
    if (t === "GuidePack") return "Guide pack";
    return t;
  }

  targetTitle(entry: ContentActivityEntry): string {
    return (
      (entry as any).targetTitle ||
      entry.contentTitle ||
      (entry as any).targetId ||
      ""
    );
  }

  canOpenTarget(entry: ContentActivityEntry): boolean {
    const t = (entry as any).targetType || entry.contentType;
    return (
      (t === "Guide" || t === "GuidePack") &&
      !!((entry as any).targetId || entry.contentId) &&
      !(entry.action || "").toLowerCase().startsWith("deleted")
    );
  }

  stateLabel(state?: string | null): string {
    switch (state) {
      case "InReview":
        return "In review";
      case "Draft":
        return "Draft";
      case "Approved":
        return "Approved";
      case "Published":
        return "Published";
      case "Rejected":
        return "Rejected";
      case "Superseded":
        return "Superseded";
      default:
        return state || "";
    }
  }

  showStateTransition(entry: ContentActivityEntry): boolean {
    return (
      entry.category === "Approval" &&
      !!entry.toState &&
      entry.fromState !== entry.toState
    );
  }

  visibleChanges(entry: ContentActivityEntry): ContentFieldChange[] {
    const changes = (entry.changes || []).filter(
      (c) => !(entry.category === "Approval" && c.field === "Approval state")
    );
    if (this.isExpanded(entry) || changes.length <= COLLAPSED_CHANGE_COUNT) {
      return changes;
    }
    return changes.slice(0, COLLAPSED_CHANGE_COUNT);
  }

  hiddenChangeCount(entry: ContentActivityEntry): number {
    const total = (entry.changes || []).filter(
      (c) => !(entry.category === "Approval" && c.field === "Approval state")
    ).length;
    return this.isExpanded(entry) ? 0 : Math.max(0, total - COLLAPSED_CHANGE_COUNT);
  }

  /** Org events (members, invites, structure) carry details without a before/after pair. */
  isDetailsOnly(entry: ContentActivityEntry): boolean {
    return (
      !entry.category &&
      !!entry.changes?.length &&
      entry.changes.every((c) => !this.hasValue(c.before))
    );
  }

  isExpanded(entry: ContentActivityEntry): boolean {
    return this.expanded.has(entry.id || "");
  }

  toggleExpanded(entry: ContentActivityEntry): void {
    const key = entry.id || "";
    if (this.expanded.has(key)) this.expanded.delete(key);
    else this.expanded.add(key);
  }

  hasValue(value?: string | null): boolean {
    return value !== null && value !== undefined && `${value}`.trim() !== "";
  }

  relativeTime(value?: string): string {
    if (!value) return "";
    const date = new Date(value.endsWith("Z") || value.includes("+") ? value : `${value}Z`);
    const diffSec = Math.round((Date.now() - date.getTime()) / 1000);
    if (Number.isNaN(diffSec)) return "";
    if (diffSec < 60) return "just now";
    const mins = Math.round(diffSec / 60);
    if (mins < 60) return `${mins} min ago`;
    const hours = Math.round(mins / 60);
    if (hours < 24) return `${hours} h ago`;
    const days = Math.round(hours / 24);
    if (days < 30) return `${days} d ago`;
    return date.toLocaleDateString();
  }

  asUtcDate(value?: string): Date | null {
    if (!value) return null;
    return new Date(value.endsWith("Z") || value.includes("+") ? value : `${value}Z`);
  }
}
